import logger from '../../utils/logger';
import React, { useState, useEffect, useRef } from 'react';
import { Bell, User, Search, LogOut, Settings, ChevronDown, HelpCircle, CreditCard, Sun, Moon } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import StatusBar from './StatusBar';
import AIToggleNavbar from '../navbar/AIToggleNavbar';
import { useTheme } from '../../context/ThemeContext';
import SearchResults from './SearchResults';
import NotificationsDropdown from './NotificationsDropdown';
import MfaNavbarIndicator from './MfaNavbarIndicator';
import BonacciStudioIcon from '../brand/BonacciStudioIcon';
import { isOnPrem } from '../../utils/featureFlags';
import { searchService } from '../../services/searchService';
import { useNotifications } from '../../contexts/NotificationsContext';
import { useDebounce } from '../../hooks/useDebounce';
import { useAuth } from '../../hooks/useAuth';

const Navbar = ({ sidebarExpanded = false }) => {
  const [searchTerm, setSearchTerm] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [isSearching, setIsSearching] = useState(false);
  const [showResults, setShowResults] = useState(false);
  const [showNotifications, setShowNotifications] = useState(false);
  const [showUserDropdown, setShowUserDropdown] = useState(false);
  const searchRef = useRef(null);
  const notificationsRef = useRef(null);
  const userDropdownRef = useRef(null);
  const debouncedSearchTerm = useDebounce(searchTerm, 300);
  const { unreadCount } = useNotifications();
  const { user, logout } = useAuth();
  const { theme, setTheme } = useTheme();
  const navigate = useNavigate();

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (searchRef.current && !searchRef.current.contains(event.target)) {
        setShowResults(false);
      }
      if (notificationsRef.current && !notificationsRef.current.contains(event.target)) {
        setShowNotifications(false);
      }
      if (userDropdownRef.current && !userDropdownRef.current.contains(event.target)) {
        setShowUserDropdown(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  useEffect(() => {
    const performSearch = async () => {
      if (debouncedSearchTerm) {
        setIsSearching(true);
        const results = await searchService.search(debouncedSearchTerm);
        setSearchResults(results);
        setShowResults(true);
        setIsSearching(false);
      } else {
        setSearchResults([]);
        setShowResults(false);
      }
    };

    performSearch();
  }, [debouncedSearchTerm]);

  return (
    <div
      className="fixed top-0 left-0 right-0 z-40 bg-white/60 dark:bg-gray-900/50 backdrop-blur-2xl border-b border-white/30 dark:border-white/[0.06]"
      style={{
        boxShadow: 'inset 0 -1px 0 0 rgba(255,255,255,0.1), 0 1px 3px rgba(15,23,42,0.04), 0 4px 24px -4px rgba(15,23,42,0.06)',
      }}
    >
      <div className={`flex h-16 items-center justify-between`}>
        {/* Logo */}
        <div className="absolute left-4 flex items-center z-50 mt-2">
          <button
            onClick={() => navigate('/')}
            className="hover:opacity-80 transition-opacity duration-200 focus:outline-none focus:ring-2 focus:ring-blue-300 rounded px-2 inline-flex items-center gap-2.5"
            aria-label="Studio — home"
          >
            <BonacciStudioIcon size={36} />
            <span
              className="text-lg tracking-tight text-gray-900 dark:text-gray-100"
              style={{
                fontFamily: '"Space Grotesk", -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif',
                fontWeight: 600,
                letterSpacing: '-0.01em'
              }}
            >
              Studio
            </span>
          </button>
        </div>

        {/* 2FA Indicator - Subtle placement between logo and right icons */}
        <div className={`flex items-center justify-center flex-1 ${
          sidebarExpanded ? 'ml-64' : 'ml-16'
        }`}>
          <MfaNavbarIndicator onNavigate={(path) => navigate(`/${path}`)} />
        </div>

        {/* Status Bar and Actions */}
        <div className="flex items-center space-x-4 px-4">
          <StatusBar />

          {/* AI Toggle */}
          <AIToggleNavbar />

          {/* Search */}
          <div className="relative" ref={searchRef}>
            <input
              type="text"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              onFocus={() => {
                if (searchResults.length > 0) {
                  setShowResults(true);
                }
              }}
              placeholder="Search..."
              className="bg-white/40 dark:bg-white/[0.06] backdrop-blur-xl text-sm rounded-xl pl-10 pr-4 py-2 w-64 focus:outline-none focus:ring-2 focus:ring-blue-400/20 dark:focus:ring-blue-500/30 focus:bg-white/60 dark:focus:bg-white/[0.1] transition-all placeholder-gray-400 dark:placeholder-gray-500 text-gray-900 dark:text-gray-100 border border-white/40 dark:border-white/[0.08]"
            />
            <Search className={`absolute left-3 top-1/2 -translate-y-1/2 ${isSearching ? 'animate-spin' : ''} text-gray-400 dark:text-gray-500`} size={16} />
            
            {/* Search Results Dropdown */}
            {showResults && (
              <SearchResults 
                results={searchResults} 
                onClose={() => {
                  setShowResults(false);
                  setSearchTerm('');
                }} 
              />
            )}
          </div>
          
          {/* Notifications */}
          <div className="relative" ref={notificationsRef}>
            <button
              className="p-2 rounded-xl hover:bg-white/50 dark:hover:bg-white/[0.06] focus:outline-none focus:ring-2 focus:ring-blue-400/20 dark:focus:ring-blue-500/20 transition-all relative text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-200"
              onClick={() => setShowNotifications(!showNotifications)}
            >
              <Bell size={20} />
              {unreadCount > 0 && (
                <span className="absolute -top-1 -right-1 bg-blue-500 dark:bg-blue-600 text-white text-xs rounded-full min-w-[18px] h-[18px] flex items-center justify-center px-1">
                  {unreadCount > 99 ? '99+' : unreadCount}
                </span>
              )}
            </button>
            
            {/* Notifications Dropdown */}
            {showNotifications && (
              <NotificationsDropdown onClose={() => setShowNotifications(false)} />
            )}
          </div>
          
          {/* User Profile */}
          <div className="relative" ref={userDropdownRef}>
            <button
              className="flex items-center space-x-1 p-1.5 rounded-xl hover:bg-white/50 dark:hover:bg-white/[0.06] focus:outline-none focus:ring-2 focus:ring-blue-400/20 dark:focus:ring-blue-500/20 transition-all text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-200"
              onClick={() => setShowUserDropdown(!showUserDropdown)}
            >
              <User size={20} />
              <ChevronDown size={14} />
            </button>

            {/* User Dropdown */}
            {showUserDropdown && (
              <div
                className="absolute right-0 mt-2 w-48 bg-white/70 dark:bg-gray-900/70 backdrop-blur-2xl rounded-xl py-1 z-[60] border border-white/40 dark:border-white/[0.08]"
                style={{
                  boxShadow: '0 4px 24px -4px rgba(15,23,42,0.12), 0 12px 48px -8px rgba(15,23,42,0.08), inset 0 1px 0 rgba(255,255,255,0.4)',
                }}
              >
                {user && (
                  <div className="px-4 py-2 border-b border-gray-100 dark:border-gray-700">
                    <p className="text-sm font-medium text-gray-900 dark:text-gray-100 truncate">{user.fullName}</p>
                    <p className="text-xs text-gray-500 dark:text-gray-400 truncate">{user.email}</p>
                  </div>
                )}

                {/* Theme Toggle */}
                <button
                  onClick={() => setTheme(theme === 'dark' ? 'light' : 'dark')}
                  className="flex w-full items-center px-4 py-2 text-sm text-gray-700 dark:text-gray-300 hover:bg-white/50 dark:hover:bg-white/[0.06]"
                >
                  {theme === 'dark' ? <Sun size={16} className="mr-2" /> : <Moon size={16} className="mr-2" />}
                  {theme === 'dark' ? 'Light Mode' : 'Dark Mode'}
                </button>

                <button
                  onClick={() => {
                    navigate('/settings');
                    setShowUserDropdown(false);
                  }}
                  className="flex w-full items-center px-4 py-2 text-sm text-gray-700 dark:text-gray-300 hover:bg-white/50 dark:hover:bg-white/[0.06]"
                >
                  <Settings size={16} className="mr-2" />
                  Settings
                </button>

                {!isOnPrem() && (
                <button
                  onClick={() => {
                    navigate('/subscription');
                    setShowUserDropdown(false);
                  }}
                  className="flex w-full items-center px-4 py-2 text-sm text-gray-700 dark:text-gray-300 hover:bg-white/50 dark:hover:bg-white/[0.06]"
                >
                  <CreditCard size={16} className="mr-2" />
                  Subscription
                </button>
                )}

                <button
                  onClick={() => {
                    alert('Reach out to support for issues/bugs');
                    setShowUserDropdown(false);
                  }}
                  className="flex w-full items-center px-4 py-2 text-sm text-gray-700 dark:text-gray-300 hover:bg-white/50 dark:hover:bg-white/[0.06]"
                >
                  <HelpCircle size={16} className="mr-2" />
                  Support
                </button>

                <button
                  onClick={async () => {
                    setShowUserDropdown(false);
                    await logout();
                    navigate('/auth');
                  }}
                  className="flex w-full items-center px-4 py-2 text-sm text-red-600 dark:text-red-400 hover:bg-white/50 dark:hover:bg-white/[0.06]"
                >
                  <LogOut size={16} className="mr-2" />
                  Sign out
                </button>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default Navbar;